import type { ConnectedAccountNanoId } from '@composio/types'
import type { ColumnDef } from '@tanstack/react-table'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMutation } from '@tanstack/react-query'
import clsx from 'clsx'
import { format } from 'date-fns'
import { Button } from 'design-system/ui/atoms/button'
import { DataTable } from 'design-system/ui/molecules/table'
import { cloneDeep } from 'lodash'
import { CopyIcon, Trash2Icon } from 'lucide-react'

import type { GetConnectedAccountsResponse } from '~/generated/client'
import { UpdateQuery } from '~/dataProcessor/hooks/global/router'
import { useApiKey } from '~/dataProcessor/hooks/user'
import { Link as ComposioLink } from '~/design-system/atom/link'
import { Switch } from '~/design-system/atom/switch'
import { ToolTipBox } from '~/design-system/atom/tooltip'
import { useToast } from '~/design-system/atom/use-toast'
import {
  deleteConnectedAccountsByNanoid,
  patchConnectedAccountsByNanoIdStatus,
} from '~/generated/client'
import { useGetAllConnections } from '~/hooks/query/connections'
import { useGetAllIntegrations } from '~/hooks/query/tools'
import { useRouterParsed } from '~/hooks/utils/useRouterParse'
import { useGetConnectorInfo } from '~/pages/connector/[connectorId]'
import { ErrorBoundary } from '~/ui/components/base/errorBoundary'
import { IdDropDown } from '~/ui/components/base/idDropDown'
import { SimpleDropdown } from '~/ui/components/custom/dropdown/simple'
import { PaginationComponent } from '~/ui/components/custom/PaginationComponent'
import { RightSideContent } from '~/ui/containers/base/base'
import { CodeWInstructionModal } from '~/ui/containers/codeModal'
import { DeletePopup } from '~/ui/containers/common/ActionButton'
import { AddAccountButton } from '~/ui/containers/common/AddAccountButton'
import { EntityIdFilter } from '~/ui/containers/common/triggerActionFilterBar'
import { copyText } from '~/utils/dom'
import { formatActionName, showFirstAndLastNLetters } from '~/utils/string'
import { CodeButton } from '../connector'

// These imports are already declared above

export const ConnectionFilter = ({
  totalPage = '',
}: {
  totalPage: string | number
}) => {
  const router = useRouter()
  const { query } = router
  const updateQuery = UpdateQuery()

  const { data: { items: connectors } = {} } = useGetAllIntegrations({
    toolkitSlug: undefined,
    page: 1,
  })
  const isConnectorPage = router.pathname === '/connector/[connectorId]'

  return (
    <div className="mt-4 flex items-center justify-between text-[13px] text-black-400">
      <div className="flex items-center gap-2">
        Filter by
        <EntityIdFilter />
      </div>
      <div className="flex items-center gap-2">
        <ShowConnectionCode />
        {!isConnectorPage && (
          <SimpleDropdown
            options={
              [
                {
                  name: 'All',
                  value: 'ALL',
                },
                ...(connectors?.map((connector) => ({
                  name: `${connector.name}`,
                  value: connector.id,
                })) || []),
              ] as {
                name: string
                value: string
              }[]
            }
            onChange={(value) => {
              updateQuery(
                'connectorId',
                value === 'ALL' ? undefined : (value as string),
              )
            }}
            value={query?.connectorId ? (query.connectorId as string) : 'ALL'}
            size={32}
            placeholder="Tool"
            className="w-[72px]"
          />
        )}

        <SimpleDropdown
          options={[
            { name: 'All', value: 'ALL' },
            { name: 'Active', value: 'ACTIVE' },
            { name: 'Initiated', value: 'INITIATED' },
            { name: 'Failed', value: 'FAILED' },
            { name: 'Expired', value: 'EXPIRED' },
          ]}
          onChange={(value) => {
            updateQuery('status', value as string)
          }}
          value={query.status as string}
          size={32}
          placeholder="Status"
          className="w-[80px]"
        />
        <PaginationComponent
          currentPage={Number(query.page) || 1}
          totalPages={Number(totalPage) || 1}
          onChange={(page) => updateQuery('page', page.toString())}
          height={32}
        />
      </div>
    </div>
  )
}

export const ConnectedAccountsScreen = () => {
  return (
    <RightSideContent
      header={
        <div className="w-full">
          <div>
            <span className="min-w-[120px]">Connected accounts</span>
          </div>
        </div>
      }
    >
      <div className="w-full px-8 py-8 pt-3">
        <ErrorBoundary
          fallback={
            <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
          }
        >
          <ConnectedAccountsRenderer />
        </ErrorBoundary>
      </div>
    </RightSideContent>
  )
}

type ConnectionItem = GetConnectedAccountsResponse['items'][number] & {
  toolkit: {
    logo?: string
  }
  is_disabled?: boolean
}

interface CellProps {
  row: {
    original: ConnectionItem
  }
}

// Cell renderer components
const ToolCell = memo(({ row }: CellProps) => {
  const connection = row.original
  return (
    <div className="flex items-center gap-2 text-[12px] font-[500] text-black-600">
      <img
        src={connection.toolkit.logo}
        alt={connection.toolkit.slug}
        className="h-[25px] w-[25px]"
      />
      {formatActionName(connection.toolkit.slug)}
    </div>
  )
})

const EntityIdCell = memo(({ row }: CellProps) => {
  const connection = row.original
  const [isCopied, setIsCopied] = useState(false)

  return (
    <div
      className="w-fit whitespace-nowrap text-[12px] font-[400] text-black-600"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <ToolTipBox
        side="right"
        align="center"
        content={isCopied ? '✅ Copied!' : 'Copy ID'}
        onOpenChange={(e) => {
          if (e === false) {
            setIsCopied(false)
          }
        }}
      >
        <div
          className="flex items-center gap-1"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            copyText(connection?.user_id ?? 'default')
            setIsCopied(true)
          }}
        >
          <span className="text-overflow-ellipsis flex max-w-full cursor-pointer items-center gap-2 overflow-hidden whitespace-nowrap">
            {showFirstAndLastNLetters(connection.user_id ?? 'default', 8)}
          </span>
          <CopyIcon
            height={12}
            width={12}
            strokeWidth={1.4}
            className="min-w-[12px]"
          />
        </div>
      </ToolTipBox>
    </div>
  )
})

const AccountIdCell = memo(({ row }: CellProps) => {
  const connection = row.original
  const [isCopied, setIsCopied] = useState(false)

  const { toast } = useToast()

  return (
    <div
      className="w-fit text-[12px] font-[400] text-black-600"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <IdDropDown
        ids={[
          {
            key: 'v1/v2',
            label: 'v1/v2 (uuid)',
            id: connection.deprecated?.uuid ?? '',
          },
          {
            key: 'v3',
            label: 'v3 (nanoid)',
            id: connection.id,
          },
        ]}
        onCopy={() => {
          toast({
            title: 'Copied',
            description: 'Connection ID copied to clipboard',
          })
        }}
      >
        <span className="flex cursor-pointer items-center gap-2">
          {showFirstAndLastNLetters(connection.id, 5)}
          <CopyIcon height={12} width={12} strokeWidth={1.4} />
        </span>
      </IdDropDown>
    </div>
  )
})

const CreatedCell = memo(({ row }: CellProps) => {
  const connection = row.original
  const createdAt = new Date(connection.created_at)
  const formattedDate = format(createdAt, 'MMM d, yyyy')
  return <div className="text-[12px]">{formattedDate}</div>
})

const StatusCell = memo(({ row }: CellProps) => {
  const connection = row.original
  return (
    <div className="text-[12px] font-[500] text-black-600">
      <div className="max-w-[72px]">
        <TagStatus status={connection.status} />
      </div>
    </div>
  )
})

const ActionsCell = memo(({ row }: CellProps) => {
  const connection = row.original
  const router = useRouter()
  const { refetch } = useGetAllConnections()
  const { toast } = useToast()
  const [isChecked, setIsChecked] = useState(!!!connection.is_disabled)

  const handleStatusChange = useCallback(
    (enabled: boolean) => {
      setIsChecked(enabled)
      patchConnectedAccountsByNanoIdStatus({
        nanoId: connection.id,
        requestBody: {
          enabled,
        },
      }).catch(() => {
        setIsChecked(!enabled)
      })
    },
    [connection.id],
  )

  return (
    <div
      className="flex items-center gap-3 pr-3"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {!connection.is_disabled ? (
        <Switch
          checked={isChecked}
          id={`switch-${connection.id}`}
          onCheckedChange={(e) => {
            handleStatusChange(e)
          }}
        />
      ) : (
        <Switch
          checked={!connection.is_disabled}
          id={`switch-${connection.id}`}
          title={`Integration is disabled`}
          onCheckedChange={(e) => {
            handleStatusChange(e)
          }}
          disabled={connection.auth_config.is_disabled}
        />
      )}
      <Button
        size="24"
        variant="secondary_outline"
        uplifted={false}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void router.push(`/connection/${connection.id}`)
        }}
      >
        <div className="mt-[2px] text-[13px] font-[500]">Open</div>
      </Button>
      <DeleteConnectionButtonV2 connectionId={connection.id} />
    </div>
  )
})

const columns: ColumnDef<ConnectionItem>[] = [
  {
    accessorKey: 'appName',
    header: 'Tool',
    size: 150,
    cell: ({ row }) => <ToolCell row={row} />,
  },
  {
    accessorKey: 'clientUniqueUserId',
    header: 'User ID',
    size: 140,
    cell: ({ row }) => <EntityIdCell row={row} />,
  },
  {
    accessorKey: 'id',
    header: 'Account ID',
    size: 210,
    cell: ({ row }) => <AccountIdCell row={row} />,
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    size: 160,
    cell: ({ row }) => <CreatedCell row={row} />,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 140,
    cell: ({ row }) => <StatusCell row={row} />,
  },
  {
    id: 'actions',
    header: () => <div className="pr-4 text-right">Actions</div>,
    size: 300,
    cell: ({ row }) => <ActionsCell row={row} />,
  },
]

export const ConnectedAccountsRenderer = () => {
  const { connections, isLoading, isError } = useGetAllConnections()
  const updateQuery = UpdateQuery()
  const router = useRouter()
  const { query } = router

  useEffect(() => {
    if (!router.isReady) return

    if (!query.page) updateQuery('page', '1')
  }, [router.query])

  const isStatusSelected = useMemo(() => {
    return !!router.query.status
  }, [router.query.status])

  const errorComponent = (
    <div className="mt-10 text-center text-red-500">
      Failed to fetch connection data. Please try again.
    </div>
  )
  // Empty state component
  const emptyState = (
    <div className="flex flex-col items-center justify-center gap-2 pb-20">
      <div className="mb-3 flex flex-col items-center gap-2">
        {isStatusSelected ? (
          <>
            <div className="text-[16px] font-[500] text-black-400">
              No connections found for the selected status
            </div>
            <div className="text-[14px] font-[400] text-black-800">
              Change the status to try again
            </div>
          </>
        ) : (
          <>
            <div className="text-[16px] font-[500] text-black-400">
              No connected account is available
            </div>
            <div className="text-center text-[14px] font-[400] text-black-800">
              <Link
                href="/apps"
                className="ds-text-blue-500 hover:ds-text-blue-600 underline underline-offset-2"
              >
                Click here
              </Link>{' '}
              to connect our first account
              <br />
              <div className="mt-2">
                This will give your agent access to that particular tool (e.g
                Github, Google Calendar)
              </div>
            </div>
          </>
        )}
      </div>
      <AddAccountButtonComp />
    </div>
  )

  return (
    <div className="h-full w-full">
      <ConnectionFilter totalPage={connections?.total_pages ?? ''} />
      <div className="mt-[18px]">
        <DataTable
          columns={columns}
          data={connections?.items ?? []}
          isLoading={isLoading}
          isError={isError}
          emptyState={emptyState}
          errorComponent={errorComponent}
          onRowClick={async (row: ConnectionItem) => {
            await router.push(`/connection/${row.id}`)
          }}
        />
      </div>
    </div>
  )
}

// Row component removed as it's no longer needed after migration to DataTable

export const DeleteConnectionButton = ({
  connectionId,
}: {
  connectionId: ConnectedAccountNanoId
}) => {
  const [open, setOpen] = useState(false)
  const { refetch } = useGetAllConnections()
  const { toast } = useToast()

  const handleDeletion = useMutation({
    mutationFn: () =>
      deleteConnectedAccountsByNanoid({
        nanoid: connectionId,
      }),
    onSuccess: async () => {
      toast({
        title: 'Success',
        description: 'Connection was deleted successfully',
      })
      setOpen(false)
      await refetch()
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error?.message,
        variant: 'destructive',
      })
      console.error('Failed to delete connection:', error)
    },
  })

  const handleDelete = () => {
    handleDeletion.mutate()
  }

  return (
    <>
      {open && (
        <DeletePopup
          handleDelete={handleDelete}
          setOpen={(e) => {
            setOpen(e)
          }}
        />
      )}
      <div
        onClick={() => {
          setOpen(true)
        }}
        className="cursor-pointer px-[4px]"
      >
        <Trash2Icon height={14} width={14} />
      </div>
    </>
  )
}

export const DeleteConnectionButtonV2 = ({
  connectionId,
}: {
  connectionId: ConnectedAccountNanoId
}) => {
  const [open, setOpen] = useState(false)
  const { refetch } = useGetAllConnections()
  const { toast } = useToast()

  const handleDeletion = useMutation({
    mutationFn: () =>
      deleteConnectedAccountsByNanoid({
        nanoid: connectionId,
      }),
    onSuccess: async () => {
      toast({
        title: 'Success',
        description: 'Connection was deleted successfully',
      })
      setOpen(false)
      await refetch()
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error?.message,
        variant: 'destructive',
      })
      console.error('Failed to delete connection:', error)
    },
  })

  const handleDelete = () => {
    handleDeletion.mutate()
  }

  return (
    <>
      {open && (
        <DeletePopup
          handleDelete={handleDelete}
          setOpen={(e) => {
            setOpen(e)
          }}
        />
      )}
      <Button
        variant="secondary_outline"
        uplifted={false}
        size="24"
        onClick={() => {
          setOpen(true)
        }}
      >
        <Trash2Icon height={14} width={14} className="mr-2" />
        Delete
      </Button>
    </>
  )
}

export const AddAccountButtonComp = () => {
  const { authConfigId } = useRouterParsed()

  const { data: connectorInfo } = useGetConnectorInfo(authConfigId)

  if (!authConfigId || !connectorInfo) return null

  return (
    <AddAccountButton buttonVariant="default" connectorData={connectorInfo} />
  )
}

// gridBox CSS removed as it's no longer needed with DataTable component

// Update TagStatus component to match API status types
const TagStatus = ({
  status,
}: {
  status: GetConnectedAccountsResponse['items'][number]['status']
}) => {
  const statusColors: Record<
    GetConnectedAccountsResponse['items'][number]['status'],
    { bg: string; border: string }
  > = {
    ACTIVE: { bg: '#63ff6608', border: '#26c129' },
    INITIALIZING: { bg: '#9797971', border: '#97979770' },
    INITIATED: { bg: '#9797971', border: '#97979770' },
    EXPIRED: { bg: '#FF636308', border: '#FF6363' },
    FAILED: { bg: '#FF636308', border: '#FF6363' },
    INACTIVE: { bg: '#FF636308', border: '#FF6363' },
  }

  const colorScheme = !!statusColors[status]
    ? statusColors[status]
    : statusColors.INITIATED

  return (
    <div
      className={clsx(
        `flex h-[24px] items-center justify-center gap-2 rounded-[16px] px-2 py-[2px] pt-[3px] text-center text-[12px] font-[500] text-black-900`,
      )}
      style={{
        backgroundColor: colorScheme?.bg,
        border: `1px solid ${colorScheme?.border}`,
      }}
    >
      {status}
    </div>
  )
}

export const sampleCodeBlockData = {
  content: {
    python: [
      {
        type: 'blockWizard',
        content: {
          heading: 'Code for getting all apps',
          blocks: [
            {
              heading: 'Fetch connections',
              text: `from composio import ComposioToolSet, App

toolset = ComposioToolSet(api_key={{api_key}})
connections = toolset.get_connected_accounts(##Insert your code here##)
print(connections)
`,
              language: 'python',
            },
          ],
        },
      },
      {
        type: 'content',
        content: (
          <div className="mt-6 text-[15px] font-[400]">
            Visit{' '}
            <ComposioLink
              className="text-[15px]"
              href="https://docs.composio.dev/patterns/Auth/connected_account"
              isExternal={true}
            >
              docs
            </ComposioLink>{' '}
            to learn more about connected accounts
          </div>
        ),
      },
    ],
    javascript: [
      {
        type: 'blockWizard',
        content: {
          heading: 'Code for getting all apps',
          blocks: [
            {
              heading: 'Fetch connections',
              text: `import {Composio} from "composio-core";
// get composio client
const composio = new Composio({apiKey: {{api_key}}});

const connections = await composio.connectedAccounts.list({##Insert your code here##})
`,
              language: 'javascript',
            },
          ],
        },
      },
      {
        type: 'content',
        content: (
          <div className="mt-6 text-[15px] font-[400]">
            Visit{' '}
            <ComposioLink
              className="text-[15px]"
              href="https://docs.composio.dev/patterns/Auth/connected_account"
              isExternal={true}
            >
              docs
            </ComposioLink>{' '}
            to learn more about connected accounts
          </div>
        ),
      },
    ],
  },
}

const ShowConnectionCode = () => {
  const [showCode, setShowCode] = useState(false)

  const apiKey = useApiKey()

  const { query } = useRouter()

  const connectorId = query.connectorId as string
  const transformedData = useMemo(() => {
    // transform data of sampleCodeBlockData
    const data = cloneDeep(sampleCodeBlockData)

    for (const language of Object.keys(data.content)) {
      // @ts-expect-error - Missing type declarations for content property
      for (const languaeItem of data.content[language]) {
        //replace blocks with api key and integration id
        const blocks = languaeItem.content.blocks
        if (!blocks) continue
        for (const block of blocks) {
          block.text = block.text.replace('{{api_key}}', `"${apiKey}"`)
          let insertContent = ``
          if (language === 'python') {
            // if(connectorId) insertContent += `\n  connector_id="${connectorId}",`
            // if(query.entityId) insertContent += `\n  entityId="${query.entityId}",`
          } else {
            if (connectorId)
              insertContent += `\n  integrationId: "${connectorId}",`
            if (query.entityId)
              insertContent += `\n  entityId: "${query.entityId.toString()}",`
          }
          block.text = block.text.replace(
            '##Insert your code here##',
            insertContent.length > 0 ? `${insertContent}\n` : '',
          )
        }
      }
    }
    return data
  }, [apiKey, connectorId, query])

  return (
    <div>
      <CodeButton setShowCode={setShowCode} />

      {showCode && (
        <CodeWInstructionModal
          title="Fetch connections with Our SDKs"
          data={transformedData}
          setOpen={() => setShowCode(false)}
        />
      )}
    </div>
  )
}
